/**
 * Slot Selection Email step — self-schedule modes only.
 * Full-width single email editor for sending the self-schedule link to the candidate.
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Flex, Text } from '@chakra-ui/react';
import type {
  EmailCompositionValue,
  EmailFieldErrors,
} from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import {
  INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE,
  type InterviewEditorAttachmentItem,
} from '../../../InterviewActivity/communication/types';
import {
  emailCompositionToCommConfig,
  attachEditorItemsToCommConfig,
  patchEmailComposition,
  getDefaultEmailComposition,
} from '../../../InterviewActivity/communication/helpers/emailUtils';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import { useScheduleSenderOptions } from '../../hooks/useScheduleSenderOptions';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/schedule.copy';
import type { ScheduleStepProps } from '../../config/schedule.types';
import {
  REQUIRED_SELF_SCHEDULE_VARIABLE,
  validateSlotSelectionEmail,
} from './slotSelectionEmail.validate';

export const SlotSelectionEmailStep = memo(
  forwardRef<StepFormApi, ScheduleStepProps>(
    (
      {
        payloadRef,
        mode: _mode,
        apiReference: _apiReference,
        prefillVersion = 0,
      },
      ref
    ) => {
      const copy = SCHEDULE_INTERVIEW_COPY.slotSelectionEmail;

      // ---- Email state ----
      const [value, setValue] = useState<EmailCompositionValue>(() => {
        const existing = payloadRef.current.slotSelectionEmail;
        if (existing && typeof existing === 'object' && 'content' in existing) {
          return existing as unknown as EmailCompositionValue;
        }
        return patchEmailComposition(getDefaultEmailComposition(), {
          from: INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE,
        });
      });
      const [attachments, setAttachments] = useState<
        InterviewEditorAttachmentItem[]
      >([]);
      const [fieldErrors, setFieldErrors] = useState<
        EmailFieldErrors | undefined
      >();

      // ---- Data fetching ----
      const { data: templateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      const { fromOptions, ccBccOptions } = useScheduleSenderOptions();

      const { selfScheduleEmail: selfScheduleVariables } =
        useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // ---- One-shot template default (create mode only) ----
      const didDefaultTemplate = useRef(false);

      useEffect(() => {
        if (prefillVersion > 0 || !templateOptions.length) return;
        if (value.template) {
          didDefaultTemplate.current = true;
          return;
        }
        if (didDefaultTemplate.current) return;
        didDefaultTemplate.current = true;
        setValue(compositionFromTemplateOption(value, templateOptions[0]));
      }, [prefillVersion, templateOptions, value]);

      // ---- Live validation after first failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!prev) return prev;
          return validateSlotSelectionEmail(value, value.from);
        });
      }, [value]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateSlotSelectionEmail(value, value.from);
            setFieldErrors(errors);
            return !errors;
          },
          syncToPayload: () => {
            const config = attachEditorItemsToCommConfig(
              emailCompositionToCommConfig(value),
              attachments
            );
            payloadRef.current.slotSelectionEmail = {
              ...config,
              _ui_template_id: value.template ?? null,
            } as unknown as Record<string, unknown>;
          },
        }),
        [attachments, payloadRef, value]
      );

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          justifyContent="center"
          overflow="auto"
          pb="8"
        >
          <Flex w="100%" maxW="720px" flexDir="column">
            <TemplatedEmail
              className="w-full"
              showFrom
              showTo
              showSubject
              enableCcBcc
              showTemplate
              showEditor
              editorVariant="richEmail"
              variables={selfScheduleVariables}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              templateOptions={templateOptions}
              fromOptions={fromOptions}
              ccBccOptions={ccBccOptions}
              applyTemplateBodyToEditor
              value={value}
              onChange={setValue}
              errors={fieldErrors}
              placeholders={{
                from: copy.placeholders.from,
                subject: copy.placeholders.subject,
                template: copy.placeholders.template,
              }}
              uploadInlineImage={uploadInlineImage}
            />
            {/* Red hint surfaces only when the validator flagged the
                missing merge tag. The validator writes a specific
                `content` error message that includes the variable
                name, so we key off that rather than introspecting
                the editor body separately. */}
            {fieldErrors?.content?.includes(
              `{{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`
            ) ? (
              <Text mt="2" fontSize="xs" color="error-200">
                Required variable:{' '}
                <strong>{`{{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`}</strong>
              </Text>
            ) : null}
          </Flex>
        </Flex>
      );
    }
  )
);

SlotSelectionEmailStep.displayName = 'SlotSelectionEmailStep';
